/**
 * Platform-wide utility for loading entity data
 *
 * Handles data loading and localStorage caching for ALL modules:
 * - Businesses (all themes)
 * - Craft fairs
 * - Any future modules
 *
 * Provides consistent localStorage pattern across the entire platform
 */

export interface EntityData {
  [key: string]: any;
}

export interface ModuleIndex {
  [entityId: string]: {
    path: string;
    name: string;
    [key: string]: any;
  };
}

/**
 * Load entity data from JSON and store in localStorage
 *
 * Pattern:
 * - Businesses: business_data_{businessId}
 * - Craft fairs: craftfair_data_{eventId}
 * - Generic: {module}_data_{entityId}
 *
 * @param module - Module name (e.g., 'businesses', 'craft-fairs')
 * @param entityId - Entity identifier (e.g., 'friendamals', 'spring-fest-2024')
 * @returns Entity data object
 * @throws Error if entity not found or data cannot be loaded
 */
export async function loadEntityData(module: string, entityId: string): Promise<EntityData> {
  try {
    // Normalize module name for storage key (remove hyphens)
    const moduleKey = module.replace(/-/g, '');
    const storageKey = `${moduleKey}_data_${entityId}`;

    const baseUrl = process.env.REACT_APP_SERVER_URL || '';

    let entityData: EntityData;

    if (module === 'businesses') {
      // Businesses: use API endpoint (hybrid DB + JSON read)
      const apiUrl = `${baseUrl}/api/businesses/${entityId}`;
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`Business data not found for '${entityId}'`);
      }
      entityData = await response.json();
    } else {
      // Other modules: still use static JSON files
      const indexPath = `${baseUrl}/app/modules/${module}/data/index.json`;
      const indexResponse = await fetch(indexPath);
      if (!indexResponse.ok) {
        throw new Error(`Index not found at ${indexPath}`);
      }

      const index: ModuleIndex = await indexResponse.json();
      if (!index[entityId]) {
        throw new Error(`Entity '${entityId}' not found in ${module} index`);
      }

      const entityInfo = index[entityId];
      const dataPath = `${baseUrl}/app/modules/${module}/data/${entityInfo.path}/data.json`;
      const dataResponse = await fetch(dataPath);
      if (!dataResponse.ok) {
        throw new Error(`Entity data not found at ${dataPath}`);
      }

      entityData = await dataResponse.json();
    }

    // Store full entity data in localStorage for agents to use
    localStorage.setItem(storageKey, JSON.stringify(entityData));
    console.log(`[loadEntityData] Stored ${module} data in localStorage: ${storageKey}`);

    return entityData;
  } catch (error) {
    console.error(`[loadEntityData] Failed to load ${module} data for '${entityId}':`, error);
    throw error;
  }
}

/**
 * Get entity data from localStorage
 *
 * @param module - Module name (e.g., 'businesses', 'craft-fairs')
 * @param entityId - Entity identifier
 * @returns Entity data object or null if not found
 */
export function getEntityDataFromCache(module: string, entityId: string): EntityData | null {
  try {
    const moduleKey = module.replace(/-/g, '');
    const storageKey = `${moduleKey}_data_${entityId}`;
    const data = localStorage.getItem(storageKey);

    if (!data) {
      return null;
    }

    return JSON.parse(data);
  } catch (error) {
    console.error(`[getEntityDataFromCache] Error reading cache for ${module}/${entityId}:`, error);
    return null;
  }
}

/**
 * Clear entity data from localStorage
 *
 * @param module - Module name
 * @param entityId - Entity identifier
 */
export function clearEntityDataCache(module: string, entityId: string): void {
  const moduleKey = module.replace(/-/g, '');
  const storageKey = `${moduleKey}_data_${entityId}`;
  localStorage.removeItem(storageKey);
  console.log(`[clearEntityDataCache] Cleared ${module} cache: ${storageKey}`);
}
